import { CONTRACT, ContractEventParams, useContract } from '@apeiron/contract'
import { CurrencyToken, useCurrencyContext } from '@apeiron/currency'
import { useMessageContext, useWeb3 } from '@apeiron/temp'
import { ApeironPleaseWait } from '@apeiron/ui'
import { useMktErrorHandling } from '@apeiron/wallet'
import useUpdateOrbitalTrackInviteSettings from '@src/hooks/graphql/useUpdateOrbitalTrackInviteSettings'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const useSaveRentalSetting = (): Hook => {
  const { t } = useTranslation()

  const { setInfo } = useMessageContext()

  const { getCurrencyAddress } = useCurrencyContext()

  const { showErrorPopup } = useMktErrorHandling()

  const { sendWithOption: doSave } = useContract(
    CONTRACT.STAR.SAVE_RENTAL_SETTING
  )

  const { toWei } = useWeb3()

  const { mutate: updateInviteSetting } = useUpdateOrbitalTrackInviteSettings()

  const saveRentalSetting = useCallback(
    async (params: SaveParameters): Promise<boolean> => {
      try {
        const {
          allowRenew,
          currency,
          groupId,
          rentTrackCount,
          rentDuration,
          rentFee,
          starId,
          addressList,
          tracksPerAddress,
          groupName
        } = params

        setInfo({
          message: (
            <ApeironPleaseWait
              subject={t('godiverse.orbital_track.saving_setting')}
            />
          )
        })

        const totalFee = toWei(rentFee * rentDuration, currency.decimals || 0)

        const isPublicSetting =
          groupId === 0 && addressList.length === 0 && tracksPerAddress === 0

        const currencyAddress = getCurrencyAddress(currency.type)

        if (isPublicSetting) {
          const result = await doSave(
            {
              verifyEvent: ContractEventParams.OrbitalTracksUpdated
            },
            starId,
            0,
            rentTrackCount,
            rentDuration,
            currencyAddress,
            totalFee,
            [],
            0,
            allowRenew
          )

          if (result) {
            setInfo(null)

            return true
          }
        } else {
          const result = await doSave(
            {
              verifyEvent: ContractEventParams.OrbitalTracksUpdated
            },
            starId,
            groupId,
            rentTrackCount,
            rentDuration,
            currencyAddress,
            totalFee,
            addressList,
            tracksPerAddress,
            allowRenew
          )

          if (!result) {
            return false
          }

          if (result.OrbitalTracksUpdated) {
            const { returnValues } = result.OrbitalTracksUpdated

            const groupId = returnValues._inviteGroupId

            const success = await updateInviteSetting({
              starID: starId.toString(),
              settings: [
                {
                  inviteGroupID: groupId.toString(),
                  groupName
                }
              ]
            })

            if (success) {
              setInfo(null)

              return true
            }
          }
        }
      } catch (error) {
        showErrorPopup(error)

        return false
      }

      setInfo(null)

      return false
    },
    [
      setInfo,
      t,
      toWei,
      getCurrencyAddress,
      doSave,
      updateInviteSetting,
      showErrorPopup
    ]
  )

  const deleteRentalSetting = useCallback(
    async (params: DeleteParameters): Promise<boolean> => {
      const { starId, groupId, currency } = params

      setInfo({
        message: (
          <ApeironPleaseWait
            subject={t('godiverse.orbital_track.deleting_setting_group')}
          />
        )
      })

      try {
        const currencyAddress = getCurrencyAddress(currency.type)

        await doSave(
          {
            verifyEvent: ContractEventParams.OrbitalTracksUpdated
          },
          starId,
          groupId,
          0,
          0,
          currencyAddress,
          0,
          [],
          0,
          false
        )

        setInfo(null)

        return true
      } catch (error) {
        showErrorPopup(error)

        return false
      }
    },
    [doSave, getCurrencyAddress, setInfo, showErrorPopup, t]
  )

  return {
    saveRentalSetting,
    deleteRentalSetting
  }
}

type SaveParameters = {
  allowRenew: boolean
  currency: CurrencyToken
  groupId: number
  rentTrackCount: number
  rentDuration: number
  rentFee: number
  starId: number
  addressList: string[]
  tracksPerAddress: number
  groupName?: string
}

type DeleteParameters = {
  starId: number
  groupId: number
  currency: CurrencyToken
}

type Hook = {
  saveRentalSetting: (params: SaveParameters) => Promise<boolean>
  deleteRentalSetting: (params: DeleteParameters) => Promise<boolean>
}

export default useSaveRentalSetting
